import { useState, useEffect, useCallback, type ReactNode } from "react"
import { GripVertical } from "lucide-react"
import { cn } from "@/lib/utils"

interface PlacementDetailsDrawerProps {
  children: ReactNode
  onClose: () => void
}

export function PlacementDetailsDrawer({ children, onClose }: PlacementDetailsDrawerProps) {
  const DEFAULT_WIDTH = 560
  const MIN_WIDTH = 420
  const MAX_WIDTH_VW = 0.8

  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [isResizing, setIsResizing] = useState(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return
    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = window.innerWidth * MAX_WIDTH_VW
      const newWidth = Math.min(
        Math.max(window.innerWidth - e.clientX, MIN_WIDTH),
        Math.min(900, maxWidth)
      )
      setWidth(newWidth)
    }
    const handleMouseUp = () => setIsResizing(false)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isResizing])

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 h-full z-50 flex"
        style={{ width: `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-label="Preview"
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            "w-2 h-full cursor-col-resize flex items-center justify-center shrink-0 group hover:bg-orange-100/50 transition-colors",
            isResizing && "bg-orange-100/60"
          )}
        >
          <GripVertical
            size={14}
            className="text-gray-300 group-hover:text-orange-400"
          />
        </div>

        <div className="flex-1 min-w-0">
          {children}
        </div>
      </div>
    </>
  )
}
